/**
 * Linked-BIM-elements section inside the issue detail panel (WHA-3521).
 *
 * Renders one chip per linked element. Clicking a chip asks "open in the 3D
 * viewer?" and, on confirm, navigates to the /model page with the owning IFC
 * loaded and the element selected (?ifc=<doc>&focus=<guid>). Each chip also
 * has an unlink control.
 *
 * Renders straight from the `elements` prop (no internal copy) so it always
 * reflects the latest hydration — the parent re-fetches via `onChange` after
 * an unlink. The parent only mounts this section when elements exist, so there
 * is no empty state here.
 */
import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Box, X } from "lucide-react";

import type { LinkedElement } from "@/domains/pm/types";
import * as pmRepo from "@/stores/pm/repository";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useModelViewerGuard } from "@/hooks/usePermissionDenied";

interface IssueElementsSectionProps {
  issueId: number;
  buildingId: number;
  /** The issue's project: opening the model viewer is checked against it. */
  projectId: number | null;
  elements: LinkedElement[];
  /** Called after an unlink so the parent can re-fetch the issue. */
  onChange?: () => void;
}

export const IssueElementsSection = observer(function IssueElementsSection({
  issueId,
  buildingId,
  projectId,
  elements,
  onChange,
}: IssueElementsSectionProps) {
  const guardModelViewer = useModelViewerGuard();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const tv = t.buildings.ifcViewer;
  const [pendingView, setPendingView] = useState<LinkedElement | null>(null);

  const handleRemove = async (anchorId: number) => {
    try {
      await pmRepo.removeIssueElement(issueId, anchorId);
      onChange?.();
    } catch (error) {
      // WHA-3633: surface failures (network / RLS) instead of a silent
      // unhandled rejection that leaves the UI stale.
      toast({
        title: tv.unlinkElementFailedTitle,
        description: error instanceof Error ? error.message : tv.tryAgain,
        variant: "destructive",
      });
    }
  };

  const confirmView = () => {
    if (!pendingView) return;
    // Load the owning IFC (when known) and select the element. The viewer
    // reads ?ifc to load the model and ?focus to select + frame it. When the
    // source doc is gone (null), fall back to ?focus alone — the viewer
    // resolves the GUID against the building's newest IFC.
    const params = new URLSearchParams();
    if (pendingView.ifc_document_id != null) {
      params.set("ifc", String(pendingView.ifc_document_id));
    }
    params.set("focus", pendingView.element_guid);
    // Always ask for the saved view. The viewer resolves it and, finding none,
    // strips the param and falls back to the ?focus behaviour above — so this
    // does not need to know whether this issue has one.
    params.set("viewpoint", String(issueId));
    navigate(`/buildings/${buildingId}/model?${params.toString()}`);
  };

  return (
    <>
      <div className="flex flex-wrap gap-1.5">
        {elements.map((el) => (
          <span
            key={el.anchor_id}
            className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-background py-1 pl-2 pr-1 text-xs hover:bg-accent"
          >
            <button
              type="button"
              onClick={() => guardModelViewer(projectId, () => setPendingView(el))}
              className="inline-flex items-center gap-1.5"
              title={`${el.element_type} · ${el.element_guid}`}
            >
              <Box className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="max-w-[180px] truncate">{el.element_name ?? el.element_type}</span>
            </button>
            <button
              type="button"
              onClick={() => handleRemove(el.anchor_id)}
              className="shrink-0 rounded-full p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
              aria-label={tv.unlinkElement}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      <ConfirmDialog
        open={pendingView != null}
        onOpenChange={(open) => {
          if (!open) setPendingView(null);
        }}
        onConfirm={confirmView}
        title={tv.openIn3dTitle}
        description={
          pendingView
            ? `${tv.viewElementDescPrefix}"${pendingView.element_name ?? pendingView.element_type}"${tv.viewElementDescSuffix}`
            : ""
        }
        confirmText={tv.openViewer}
        confirmVariant="default"
      />
    </>
  );
});
